import type { ComponentProps } from "react";
import { useId } from "react";
import { tv } from "tailwind-variants";

const STAR_PATH =
  "M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z";

const STAR_COUNT = 5;
const STAR_SIZE = 20;
const STAR_GAP = 2;
const STEP = STAR_SIZE + STAR_GAP;
const WIDTH = STAR_COUNT * STAR_SIZE + (STAR_COUNT - 1) * STAR_GAP;

const starsVariants = tv({
  base: "inline-block shrink-0",
});

export type StarsProps = {
  /** 0 to 5; a fraction fills that share of the next star. */
  rating: number;
  /** Height in px; the width follows. */
  size?: number;
} & Omit<ComponentProps<"svg">, "children">;

export const Stars = ({ className, rating, size = 16, ...props }: StarsProps) => {
  const clipId = `stars-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const clamped = Math.min(Math.max(rating, 0), STAR_COUNT);
  const fullStars = Math.floor(clamped);
  const filledWidth = fullStars * STEP + (clamped - fullStars) * STAR_SIZE;

  const stars = Array.from({ length: STAR_COUNT }, (_, index) => {
    return <path d={STAR_PATH} key={index} transform={`translate(${index * STEP} 0)`} />;
  });

  return (
    <svg
      aria-label={`Rated ${clamped.toFixed(1)} out of 5`}
      className={starsVariants({ className })}
      height={size}
      role="img"
      viewBox={`0 0 ${WIDTH} ${STAR_SIZE}`}
      width={Math.round((size * WIDTH) / STAR_SIZE)}
      {...props}
    >
      <defs>
        <clipPath id={clipId}>
          <rect height={STAR_SIZE} width={filledWidth} x="0" y="0" />
        </clipPath>
      </defs>
      <g className="fill-line">{stars}</g>
      <g className="fill-content" clipPath={`url(#${clipId})`}>
        {stars}
      </g>
    </svg>
  );
};
